import { value } from '@takeshape/util';
import { produce } from 'immer';
import get from 'lodash/get';
import set from 'lodash/set';
import unset from 'lodash/unset';
import { type ActionType, createReducer } from 'typesafe-actions';
import type { replaceAsset } from '../asset-uploader/actions';
import { logoutUser } from '../auth/actions';
import { postSchemaRoutine } from '../branches/sagas/post-schema';
import { updateSchema } from '../branches/sagas/update-schema';
import type { NormalizedContentById } from '../common/utils/graphql-helpers/normalize-response';
import {
  type Actions,
  contentLocked,
  contentUnlocked,
  createContent,
  deleteContent,
  editingSession,
  type GetContentMeta,
  getContent,
  invalidateContent,
  lockBroken,
  lockContent,
  unlockContent,
  updateContent
} from './actions';
import type { AllContentState, ContentId, ContentState, EditingSession, LocaleState } from './types';

export const defaultSession: EditingSession = {
  locking: false,
  unlocking: false,
  active: false,
  ready: false,
  lock: null
};

export const defaultContentState: ContentState = {
  data: undefined,
  loading: false,
  loaded: false,
  updating: false
};

export const allContentInitialState: AllContentState = {
  contentByContentType: {},
  editingSessions: {},
  creating: [],
  isAssetPickerOpen: false
};

function mergeNewContent(
  state: NormalizedContentById,
  payload: NormalizedContentById,
  locale: string
): NormalizedContentById {
  return produce(state, (draft) => {
    for (const shapeId of Object.keys(payload)) {
      // Payload[contentTypeId] is one of:
      // - ContentEntry: has an _id attribute
      // - Object<ContentId: ContentEntry>: does not have an _id attribute
      if (payload[shapeId]._id) {
        set(draft, [shapeId, locale], {
          ...defaultContentState,
          data: payload[shapeId],
          loaded: true,
          loading: false
        });
      }

      for (const contentId of Object.keys(payload[shapeId])) {
        set(draft, [shapeId, contentId, locale], {
          ...defaultContentState,
          data: payload[shapeId][contentId],
          loaded: true,
          loading: false
        });
      }
    }
  });
}

function mergeContent(state: AllContentState, meta: GetContentMeta, obj: Partial<ContentState>): AllContentState {
  return produce(state, (draft) => {
    const path =
      !meta.isSingleton && meta.contentId ? [meta.shapeId, meta.contentId, meta.locale] : [meta.shapeId, meta.locale];
    const content = get(draft.contentByContentType, path) || defaultContentState;
    set(draft.contentByContentType, path, { ...content, ...obj });
  });
}

function mergeSession(state: AllContentState, ids: ContentId, data: Partial<EditingSession>): AllContentState {
  const { contentId, shapeId } = ids;
  return produce(state, (draft) => {
    const session = draft.editingSessions[contentId];
    draft.editingSessions[contentId] = {
      ...(session || { ...defaultSession, contentId, shapeId }),
      ...data
    };
  });
}

type AllActions =
  | Actions
  | ActionType<typeof logoutUser.fulfilled>
  | ActionType<typeof updateSchema.fulfilled>
  | ActionType<typeof postSchemaRoutine.fulfilled>
  | ActionType<typeof replaceAsset.fulfilled>;

export default createReducer<AllContentState, AllActions>(allContentInitialState)
  // GET
  .handleAction(getContent.pending, (state, { meta }) => mergeContent(state, meta, { loading: true }))
  .handleAction(getContent.fulfilled, (state, { payload, meta: { locale } }) => ({
    ...state,
    contentByContentType: mergeNewContent(state.contentByContentType, payload.response, locale)
  }))
  .handleAction(getContent.rejected, (state, { meta, payload }) =>
    mergeContent(state, meta, { loading: false, error: payload })
  )
  // CREATE
  .handleAction(createContent.pending, (state, { meta }) => ({
    ...state,
    creating: state.creating.concat(meta.source)
  }))
  .handleAction(createContent.fulfilled, (state, { payload, meta }) => ({
    ...state,
    contentByContentType: mergeNewContent(state.contentByContentType, payload.response, meta.locale),
    creating: state.creating.filter((source) => source !== meta.source)
  }))
  .handleAction(createContent.rejected, (state, { meta }) => ({
    ...state,
    creating: state.creating.filter((source) => source !== meta.source)
  }))
  // UPDATE
  .handleAction(updateContent.pending, (state, { meta }) => mergeContent(state, meta, { updating: true }))
  .handleAction(updateContent.fulfilled, (state, { payload, meta }) => ({
    ...state,
    contentByContentType: mergeNewContent(state.contentByContentType, payload.response, meta.locale)
  }))
  .handleAction(updateContent.rejected, (state, { meta, payload }) =>
    mergeContent(state, meta, { updating: false, error: payload })
  )
  // DELETE
  .handleAction(deleteContent.pending, (state, { meta }) => mergeContent(state, meta, { updating: true }))
  .handleAction(deleteContent.fulfilled, (state, { meta }) =>
    produce(state, (draft) => {
      const shapeContent = draft.contentByContentType[meta.shapeId];
      if (shapeContent && meta.contentId) {
        delete shapeContent[meta.contentId];
      }
    })
  )
  .handleAction(deleteContent.rejected, (state, { payload, meta }) =>
    mergeContent(state, meta, { updating: false, error: payload })
  )
  // SERVERSIDE LOCK ACTIONS
  .handleAction(contentLocked, (state, { payload }) =>
    mergeSession(state, { shapeId: payload.shapeId, contentId: payload.contentId }, { lock: payload.lock })
  )
  .handleAction(contentUnlocked, (state, { payload }) =>
    mergeSession(state, { shapeId: payload.shapeId, contentId: payload.contentId }, { lock: null })
  )
  .handleAction(lockBroken, (state, { payload }) =>
    mergeSession(state, { shapeId: payload.shapeId, contentId: payload.contentId }, { ready: false, active: false })
  )
  // EDITING SESSION
  .handleAction(editingSession.pending, (state, { payload }) => mergeSession(state, payload, { active: true }))
  .handleAction(editingSession.cancelled, (state, { payload }) =>
    mergeSession(state, payload, { ready: false, active: false })
  )
  .handleAction(editingSession.fulfilled, (state, { payload }) => mergeSession(state, payload, { ready: true }))
  // LOCK CONTENT
  .handleAction(lockContent.pending, (state, { meta }) => mergeSession(state, meta, { locking: true }))
  .handleAction(lockContent.fulfilled, (state, { meta, payload }) =>
    mergeSession(state, meta, {
      locking: false,
      lock: payload.lock
    })
  )
  .handleAction(lockContent.rejected, (state, { meta }) => mergeSession(state, meta, { locking: false }))
  // UNLOCK CONTENT
  .handleAction(unlockContent.pending, (state, { meta }) => mergeSession(state, meta, { unlocking: true }))
  .handleAction(unlockContent.fulfilled, (state, { meta }) => mergeSession(state, meta, { unlocking: false }))
  .handleAction(unlockContent.rejected, (state, { meta }) => mergeSession(state, meta, { unlocking: false }))
  .handleAction(logoutUser.fulfilled, value(allContentInitialState))
  .handleAction(updateSchema.fulfilled, value(allContentInitialState))
  .handleAction(postSchemaRoutine.fulfilled, value(allContentInitialState))
  .handleAction(invalidateContent, (state, { payload: { shapeId, isSingleton, contentId, contentVersion } }) =>
    produce(state, (draft) => {
      const someKindOfState = draft.contentByContentType[shapeId];
      const localeState = isSingleton
        ? (someKindOfState as LocaleState)
        : (someKindOfState?.[contentId] as LocaleState);
      if (localeState) {
        const langs = Object.keys(localeState);
        for (const lang of langs) {
          const langVersion = localeState[lang].data?._version;
          if (langVersion && langVersion !== contentVersion) {
            unset(
              draft,
              isSingleton ? ['contentByContentType', shapeId, lang] : ['contentByContentType', shapeId, contentId, lang]
            );
          }
        }
      }
    })
  );
